import { useState, useEffect } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { useDarkMode } from '@app/utils';
import { ThemedView, Theme } from '@app/theme';
import ScreenWrapper from '@hoc/ScreenWrapper';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { PrivateStackParamList } from '../../navigationApp/types';
import KeyboardDismissView from '@hoc/KeyboardDismissView';
import CommonHeader from '../../sharedComponents/Header/Header';
import Stepper from '@app/sharedComponents/Stepper/Stepper';
import { fetchBankAccounts, fetchBankAccountsFromApplication, getStepper, verifyBankAccount } from './services/verifyBankService';
import { useAppSelector, useAppDispatch } from '@app/store/hooks';
import { Bank } from './types/bankVerify';
import NoAccountFound from './component/NoAccountFound';
import { useTheme } from '@shopify/restyle';
import AccountList from './component/AccountList';
import { trackEvent } from '@app/utils/amplitude';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import { get as getLodash } from 'lodash';
import { updateCurrentStepId, updateLinkedApplication } from '@app/store/slices/authSlice';
import { getNextRoute } from '@app/navigationApp/navigationUtil';

// modal imports
import BankErrorModal from './../BankAccountAdd/components/BankErrorModal'
import BankPennyDropModal from './../BankAccountAdd/components/BankPennyDropModal'
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet'
import BankSuccessModal from './../BankAccountAdd/components/BankSuccessModal'
import BankIfscUnsupportedModal from './../BankAccountAdd/components/BankIfscUnsupportedModal'
import { useStepperData } from '@app/hooks/useStepperData';
import { updatePartnerParams } from '@app/store/slices/platformSlice';
import { CreditApplicationSubType, CreditApplicationType } from '@app/configs/constants';
import rupeeLottie from '../../assets/lotties/Penny_drop.json'


type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'BANK_ACCOUNT_VERIFICATION'>;

type ModalState = 'error' | 'success' | 'penny' | 'idle' | 'ifsc_unsupported'



const VerifyBankAccount = ({ navigation, route }: ScreenProps) => {
  const isDarkMode = useDarkMode();
  const userDetails = useAppSelector(state => state.auth.userDetails);
  const platformParams = useAppSelector(state => state.platformParams);
  const { colors } = useTheme<Theme>();
  const dispatch = useAppDispatch();

  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [bankAccounts, setBankAccounts] = useState<Bank[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifyingBankAccount, setIsVerifyingBankAccount] = useState(false);
  const isFromJupiter = platformParams?.platform === 'JUPITER';
  const [modalState, setModalState] = useState<ModalState>('idle');
  // const [rupeeLottie, setRupeeLottie] = useState<any>(null);
  const [successLottie, setSuccessLottie] = useState<any>(null);
  const [isUserUpdatingMandate, setIsUserUpdatingMandate] = useState(
    userDetails?.linkedApplications?.[0]?.applicationType === CreditApplicationType.CREDIT_LOAN_SERVICING_APPLICATION_AGAINST_SECURITIES 
    && userDetails?.linkedApplications?.[0]?.creditApplicationSubType === CreditApplicationSubType.MANDATE_UPDATE
  );



  const { stepperData, isLoading: stepperLoading } = useStepperData();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  const fetchBankAccount = async () => {
    try {
      const response = await fetchBankAccounts({ applicationId: userDetails?.linkedApplications[0]?.applicationId });
      setBankAccounts(response?.stepResponseObject);
    } catch (error) {
      console.error('bank account fetch error', error);
    } finally {
    }
  }

  const fetchBankAccountFromApplication = async () => {
    try {
      const response = await fetchBankAccountsFromApplication({ applicationId: userDetails?.linkedApplications[0]?.applicationId });
      setBankAccounts(response.existingBankAccounts);
    } catch (error) {
      console.error('bank account fetch error', error);
    } finally {
    }
  }


  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      isUserUpdatingMandate ? fetchBankAccountFromApplication() : fetchBankAccount(),
    ]).finally(() => {
      setIsLoading(false);
    })
    dispatch(updatePartnerParams({showCall:true}))
  }, []);

  useEffect(() => {
    // fetch("https://lottie.host/78420adf-df0a-4fa2-a500-36bbf2ac0762/YkUlAx1j0n.json", {
    //   method: "GET",
    // })
    //   .then((response) => response.json())
    //   .then((responseData) => {
    //     setRupeeLottie(responseData)
    //   })
    //   .catch((error) => {
    //     console.error(error);
    //   })

    fetch("https://lottie.host/34e6bfb8-a91f-4e4f-8856-7571d41cee8e/6sPdPdx4Yf.json", {
      method: "GET",
    })
      .then((response) => response.json())
      .then((responseData) => {
        setSuccessLottie(responseData)
      })
      .catch((error) => {
        console.error(error);
      })
  }, [])


  const handleAddBankAccount = () => {
    trackEvent('VERIFY_BANK_ADD_ANOTHER_BANK_BUTTON_CLICKED');
    navigation.navigate('BANK_SELECT');
  }

  const handleVerifyBankAccount = async () => {
    setIsVerifyingBankAccount(true)
    trackEvent('VERIFY_BANK_BUTTON_CLICKED')
    setModalState('penny')
    try {
      const response = await verifyBankAccount({
        applicationId: userDetails?.linkedApplications[0]?.applicationId,
        bankAccountNumber: selectedBank?.accountNumber!,
        bankIfscCode: selectedBank?.ifscCode!
      })
      const stepResponseObject = response?.data?.stepResponseObject

      if (stepResponseObject?.failureDetails?.failureType === 'NAME_MISMATCH') {
        // close modal
        setModalState('idle')
        setTimeout(() => {
          navigation.navigate('NAME_MISMATCH', {
            bankCode: selectedBank?.ifscCode.slice(0, 4)!,
          })
        }, 500)
      } else if (stepResponseObject?.failureDetails?.failureType === 'IFSC_INELIGIBLE_FOR_AUTOPAY') {
        // show error modal
        setModalState('ifsc_unsupported')
      }
      else {
        dispatch(updateLinkedApplication(response?.data?.updatedApplicationObj))
        const currentStepId = getLodash(response, 'data.updatedApplicationObj.currentStepId', null)
        dispatch(updateCurrentStepId(currentStepId))
        setModalState('success')
        trackEvent('BANKVERIFY_SUCCESSBOTTOMSHEET_PAGEVIEW')
      }

    } catch (error:any) {
      setModalState('idle')
      if(error?.data?.statusCode === "500"){
        return;
      }
      setModalState('error')
      setErrorMessage(error?.data?.message || 'Something went wrong')
    } finally {
      setIsVerifyingBankAccount(false)
    }
  }

  const navigateToNextScreen = () => {
    if(Platform.OS !== 'ios') {
    setModalState('idle')

    }
    trackEvent('BANKVERIFY_SUCCESSBOTTOMSHEET_CONTINUE_CLICKED')
    navigation.reset({
        index: 0,
        routes: [{ name: 'KYC_STEPPER' }, { name: getNextRoute(userDetails?.linkedApplications[0].currentStepId, userDetails?.linkedApplications[0].applicationState, userDetails?.linkedApplications[0].stepStatusMap) }]
      })  
  }

  const handleAddAnotherBankAccount = () => {
    setModalState('idle')
    navigation.navigate('BANK_SELECT')
  }



  return (
    <ScreenWrapper screenName={'VERIFY_BANK_START'} additionalProperties={{ newArchRoute: true }}>
      <KeyboardDismissView>
        <ThemedView flex={1}
          bg='bg-surface-primary'
        >
          <CommonHeader
            navigation={navigation}
            pageName={route.name}
            showNeedHelp={true}
            hideBottomBorder={true}
            label={
              isUserUpdatingMandate
              ? 'Update bank account'
              : 'Link bank account'
            }
          />
          <Stepper isLoading={stepperLoading} stepperData={stepperData} />
          <ThemedView
            flex={1}
          >
            {isLoading ? (
              <></>
            ) : bankAccounts && bankAccounts?.length > 0 ? (
              // bank accounts found
              <AccountList isVerifyingBankAccount={isVerifyingBankAccount} handleVerifyBankAccount={handleVerifyBankAccount} handleAddBankAccount={handleAddBankAccount} setSelectedBank={setSelectedBank} bankAccounts={bankAccounts} selectedBank={selectedBank} navigation={navigation} isDarkMode={isDarkMode} />
            ) : (
              // zero bank accounts
              <NoAccountFound navigation={navigation} handleAddBankAccount={handleAddBankAccount} />
            )}
          </ThemedView>
        </ThemedView>
      </KeyboardDismissView>
      <BottomSheetContainer
        isVisible={modalState !== 'idle'}
        close={() => setModalState('idle')}
        onClose={() => setModalState('idle')}
        dragComponent={<View style={{ width: 40, height: 4, backgroundColor: colors['border-primary'], borderRadius: 10 }} />}
        containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 }}
        closeOnBackdropPress={true}
        borderTopRightRadius={10}
        borderTopLeftRadius={10}
      >
        {modalState === 'error' && <BankErrorModal errorMessage={errorMessage ?? ''} onPress={handleAddAnotherBankAccount} />}
        {modalState === 'success' && <BankSuccessModal lottie={successLottie} onPress={navigateToNextScreen} />}
        {modalState === 'penny' && <BankPennyDropModal lottie={rupeeLottie} />}
        {modalState === 'ifsc_unsupported' && <BankIfscUnsupportedModal />}
      </BottomSheetContainer>
    </ScreenWrapper>
  );
};


export default VerifyBankAccount; 